import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  setDateFilter,
  setFilterBy,
} from "src/redux/slices/PanFormReportSlice";
import { AppDispatch, RootState } from "src/redux/store";
import { date, object } from "yup";
import PanFormReportFilterCard from "./PanFormReportFilterCard";

type Props = {
  close: () => void;
};

export type FormInitialValues = {
  status: string;
  applicationType: string;
  startDate: string | null;
  endDate: string | null;
};

/**
 * Filters for the PAN Form Download Report: application status, application
 * type and the applied-date range. All three already travel to
 * `/pan-form/pending-list` (and the XLSX export) through the slice's `filterBy`
 * / `dateFilter`, so this only has to write them there — the listing and the
 * export then follow the same filtered set.
 */
const PanFormReportFilterCardWrapper = ({ close }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const panFormReportState: any = useSelector(
    (state: RootState) => state.panFormReport
  );
  const { filterBy, dateFilter } = panFormReportState;

  const valueOf = (fieldName: string) =>
    filterBy?.find((filter: any) => filter.fieldName === fieldName)
      ?.value?.[0] || "";

  const initialValues: FormInitialValues = {
    status: valueOf("status"),
    applicationType: valueOf("applicationType"),
    startDate: dateFilter?.start_date || null,
    endDate: dateFilter?.end_date || null,
  };

  const validationSchema = object({
    startDate: date()
      .nullable()
      .max(moment(), "Date should not be after current date"),
    endDate: date()
      .nullable()
      .when("startDate", (startDate, schema) =>
        startDate ? schema.min(startDate, "Must be after start date") : schema
      ),
  });

  const handleSubmit = async (
    values: FormInitialValues,
    { setSubmitting }: FormikHelpers<FormInitialValues>
  ) => {
    setSubmitting(false);
    dispatch(
      setDateFilter({
        start_date: values.startDate
          ? moment(values.startDate).format("yyyy-MM-DD")
          : "",
        end_date: values.endDate
          ? moment(values.endDate).format("yyyy-MM-DD")
          : "",
      })
    );
    await dispatch(
      setFilterBy(
        filterBy?.map((filter: any) => {
          switch (filter.fieldName) {
            case "status":
              return { ...filter, value: values.status ? [values.status] : [] };
            case "applicationType":
              return {
                ...filter,
                value: values.applicationType ? [values.applicationType] : [],
              };
            default:
              return filter;
          }
        })
      )
    );
    close();
  };

  const handleReset = async (formikProps: FormikProps<FormInitialValues>) => {
    await dispatch(setDateFilter({ start_date: "", end_date: "" }));
    await dispatch(
      setFilterBy(
        filterBy?.map((filter: any) =>
          filter.fieldName === "status" || filter.fieldName === "applicationType"
            ? { ...filter, value: [] }
            : filter
        )
      )
    );
    formikProps.resetForm();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form>
          <PanFormReportFilterCard
            formikProps={formikProps}
            onReset={() => handleReset(formikProps)}
          />
        </Form>
      )}
    </Formik>
  );
};

export default PanFormReportFilterCardWrapper;
